import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useGetReportQuery } from '../../../store/api/reportsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { getReportById } from '../config/reportCatalog';
import { exportReportToPdf, exportReportToExcel } from '../utils/reportExport';

function ReportView() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(0);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  const reportMeta = getReportById(reportId);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const { data, isLoading, error } = useGetReportQuery(
    { reportType: reportId, fromDate, toDate },
    { skip: !reportId }
  );

  const formatAmount = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '–';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  };

  const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '–');

  const periodText = `${formatDate(fromDate)} – ${formatDate(toDate)}`;
  const safeFilename = (reportId || 'report').replace(/[^a-z0-9-_]/gi, '_');

  const handleExportPdf = () => {
    if (!data) return;
    exportReportToPdf({
      title: reportMeta?.label,
      companyName: company?.name,
      period: periodText,
      columns: data.columns || [],
      rows: data.rows || [],
      summaryItems: summaryItems.map((s) => ({ label: s.label, value: s.value })),
      filename: `${safeFilename}_${fromDate}_${toDate}`,
    });
  };

  const handleExportExcel = () => {
    if (!data) return;
    exportReportToExcel({
      title: reportMeta?.label,
      companyName: company?.name,
      period: periodText,
      columns: data.columns || [],
      rows: data.rows || [],
      summaryItems: summaryItems.map((s) => ({ label: s.label, value: s.value })),
      filename: `${safeFilename}_${fromDate}_${toDate}`,
      sheetName: (reportMeta?.label || 'Report').slice(0, 31),
    });
  };

  const handleBack = () => {
    // Prefer history back so we return to category page like /reports/category/inventory
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/reports');
  };

  if (!reportMeta) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Report not found.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ borderRadius: 2 }}
        >
          Back to Reports
        </Button>
      </Box>
    );
  }

  const summaryItems = data?.summary
    ? [
        typeof data.summary.totalValue === 'number' && {
          label: 'Total Value',
          value: formatAmount(data.summary.totalValue),
        },
        typeof data.summary.totalSales === 'number' && {
          label: 'Total Sales',
          value: formatAmount(data.summary.totalSales),
        },
        typeof data.summary.totalPurchase === 'number' && {
          label: 'Total Purchase',
          value: formatAmount(data.summary.totalPurchase),
        },
        typeof data.summary.totalExpense === 'number' && {
          label: 'Total Expense',
          value: formatAmount(data.summary.totalExpense),
        },
        typeof data.summary.totalOutstanding === 'number' && {
          label: 'Outstanding',
          value: formatAmount(data.summary.totalOutstanding),
        },
        typeof data.summary.totalBalance === 'number' && {
          label: 'Total Balance',
          value: formatAmount(data.summary.totalBalance),
        },
        typeof data.summary.totalDebit === 'number' && {
          label: 'Total Debit',
          value: formatAmount(data.summary.totalDebit),
        },
        typeof data.summary.totalCredit === 'number' && {
          label: 'Total Credit',
          value: formatAmount(data.summary.totalCredit),
        },
        typeof data.summary.closingBalance === 'number' && {
          label: 'Closing Balance',
          value: formatAmount(data.summary.closingBalance),
        },
        typeof data.summary.totalTaxable === 'number' && {
          label: 'Total Taxable',
          value: formatAmount(data.summary.totalTaxable),
        },
        typeof data.summary.totalInputVat === 'number' && {
          label: 'Total Input VAT',
          value: formatAmount(data.summary.totalInputVat),
        },
        typeof data.summary.totalOutputVat === 'number' && {
          label: 'Total Output VAT',
          value: formatAmount(data.summary.totalOutputVat),
        },
        typeof data.summary.totalReturns === 'number' && {
          label: 'Total Returns',
          value: formatAmount(data.summary.totalReturns),
        },
        typeof data.summary.totalNetPurchase === 'number' && {
          label: 'Net Purchase',
          value: formatAmount(data.summary.totalNetPurchase),
        },
        typeof data.summary.totalPaid === 'number' && {
          label: 'Total Paid',
          value: formatAmount(data.summary.totalPaid),
        },
        typeof data.summary.totalRevenue === 'number' && {
          label: 'Total Revenue',
          value: formatAmount(data.summary.totalRevenue),
        },
        typeof data.summary.totalCost === 'number' && {
          label: 'Total Cost',
          value: formatAmount(data.summary.totalCost),
        },
        typeof data.summary.totalProfit === 'number' && {
          label: 'Total Profit',
          value: formatAmount(data.summary.totalProfit),
        },
        typeof data.summary.netVat === 'number' && {
          label: 'Net VAT (Payable / Receivable)',
          value: formatAmount(data.summary.netVat),
        },
        typeof data.summary.totalDiscount === 'number' && {
          label: 'Total Discount',
          value: formatAmount(data.summary.totalDiscount),
        },
        data.summary.ordersWithDiscount != null && {
          label: 'Orders with Discount',
          value: data.summary.ordersWithDiscount.toLocaleString(),
        },
        data.summary.totalRows != null && {
          label: reportId === 'out-of-stock' ? 'Out of stock items' : 'Records',
          value: data.summary.totalRows.toLocaleString(),
        },
        data.summary.totalOrders != null && {
          label: 'Total Orders',
          value: data.summary.totalOrders.toLocaleString(),
        },
        data.summary.slowMovingCount != null && {
          label: 'Slow Moving',
          value: data.summary.slowMovingCount.toLocaleString(),
        },
        data.summary.fastMovingCount != null && {
          label: 'Fast Moving',
          value: data.summary.fastMovingCount.toLocaleString(),
        },
        typeof data.summary.totalQuantity === 'number' && {
          label: 'Total Qty Sold',
          value: data.summary.totalQuantity.toLocaleString(),
        },
      ].filter(Boolean)
    : [];

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        '@media print': { bgcolor: 'transparent', p: 0, '& .no-print': { display: 'none' } },
      }}
    >
      {/* Header */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' },
            }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}>
              {reportMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {company?.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card
        className="no-print"
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <CalendarMonthOutlinedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            <TextField
              type="date"
              label="From"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 168 }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 168 }}
            />
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPdf}
              disabled={!data}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: 1 },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChartIcon />}
              onClick={handleExportExcel}
              disabled={!data}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: 1 },
              }}
            >
              Export Excel
            </Button>
          </Box>
        </CardContent>
      </Card>

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading report…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { fontWeight: 500 },
          }}
        >
          {error?.data?.message || 'Failed to load report'}
        </Alert>
      )}

      {data && !isLoading && (
        <>
          {/* Report period (print) */}
          <Box sx={{ mb: 2, '@media print': { mb: 1 } }}>
            <Typography variant="body2" color="text.secondary">
              Period: {formatDate(fromDate)} – {formatDate(toDate)}
            </Typography>
          </Box>

          {/* Summary */}
          {summaryItems.length > 0 && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <SummarizeOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Summary
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {summaryItems.map((item) => (
                    <Chip
                      key={item.label}
                      label={`${item.label}: ${item.value}`}
                      size="medium"
                      sx={{
                        fontWeight: 600,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {data.message && (
            <Alert
              severity="info"
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {data.message}
            </Alert>
          )}

          {/* Table */}
          {data.rows && data.rows.length > 0 && data.columns && data.columns.length > 0 && (() => {
            const normalizedColumns = data.columns.map((col, i) =>
              typeof col === 'string'
                ? { id: col, label: col }
                : { id: col.id ?? col.label ?? `col_${i}`, label: col.label ?? col.id ?? `Column ${i + 1}` }
            );
            return (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {normalizedColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          color: 'text.secondary',
                          bgcolor: alpha(theme.palette.grey[500], 0.08),
                          py: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={{
                        bgcolor: idx % 2 === 1 ? alpha(theme.palette.grey[500], 0.03) : 'transparent',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      {normalizedColumns.map((col) => {
                        const raw = row[col.id];
                        let display = raw;
                        const colLower = col.id.toLowerCase();
                        const isQuantity =
                          typeof raw === 'number' &&
                          (colLower.includes('quantity') ||
                            colLower.includes('qty') ||
                            colLower === 'totalquantity' ||
                            colLower === 'quantitysold' ||
                            colLower === 'totalsold');
                        const isCurrency =
                          typeof raw === 'number' &&
                          !isQuantity &&
                          (colLower.includes('amount') ||
                            colLower.includes('value') ||
                            colLower.includes('revenue') ||
                            colLower.includes('cost') ||
                            colLower === 'profit' ||
                            colLower === 'debit' ||
                            colLower === 'credit' ||
                            colLower === 'balance' ||
                            colLower === 'subtotal' ||
                            (colLower.includes('total') && colLower !== 'totalrows'));
                        if (isCurrency) {
                          display = formatAmount(raw);
                        } else if (typeof raw === 'number' && (colLower.includes('margin') || colLower === 'vatrate')) {
                          display = `${Number(raw).toFixed(1)}%`;
                        } else if (
                          raw &&
                          (colLower.includes('date') || col.id === 'date')
                        ) {
                          display = formatDate(raw);
                        }
                        return (
                          <TableCell
                            key={col.id}
                            sx={{
                              py: 1.5,
                              fontWeight:
                                isCurrency || colLower.includes('value') || colLower.includes('margin') ? 600 : 400,
                              borderColor: 'divider',
                            }}
                          >
                            {display != null && display !== '' ? String(display) : '–'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            );
          })()}

          {data.rows && data.rows.length === 0 && !data.message && (
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                py: 6,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No data for the selected period.
              </Typography>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

export default ReportView;
